import { useEffect, useState } from "react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { contactsApi } from "./contacts-api";
import { companiesApi } from "./companies-api";
import { listTime, showConversation, type Conversation, type Message } from "./inbox-api";
import { channelLabel, describeAds, type Ads, type SourceFirst } from "./ads";
import { locale, t } from "@/lib/i18n";

/* Карточка клиента: все диалоги с ним по всем каналам в хронологии (WhatsApp, Telegram, архив Wazzup; дальше почта и звонки)
   и поиск фрагмента по всей переписке; блок «Откуда клиент» — реклама и источник простыми словами (сквозная аналитика, шаг 1). */

const input =
  "h-8 w-full rounded-btn border border-border bg-background px-2.5 text-[12px] text-foreground outline-none transition focus:border-primary";

const icon = (kind: string) =>
  kind === "telegram" ? "ri-telegram-line text-sky-600" : kind === "email" ? "ri-mail-line text-muted-foreground" : kind === "wazzup" ? "ri-archive-line text-muted-foreground" : kind === "phone" ? "ri-phone-line text-muted-foreground" : "ri-whatsapp-line text-emerald-600";

const open = (id: string) => {
  showConversation(id);
  goAnchor("#inbox-app");
};

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

/** откуда взялась сама карточка (contacts.source) — чтобы было понятно, почему у контакта нет меток или ответственного */
const ORIGIN: Record<string, string> = {
  wazzup: t("из архива переписки Wazzup (Zoho CRM)"),
  import: t("из импорта Zoho CRM"),
  zoho_crm: t("из Zoho CRM"),
  whatsapp: t("из переписки WhatsApp"),
  telegram: t("из переписки Telegram"),
  email: t("из письма"),
  manual: t("создана вручную в Hub"),
  csv: t("из файла"),
};
const FROM: Record<string, string> = {
  zoho_contact: t("записи контакта в Zoho CRM"),
  zoho_deal: t("сделки в Zoho CRM"),
  zoho_lead: t("лида в Zoho CRM"),
  site: t("меток сайта"),
};

export function WhereFrom({ ads, first, origin }: { ads: Ads | null | undefined; first: SourceFirst; origin?: string }) {
  const lines = describeAds(ads, first);
  const hasAds = Boolean(ads && Object.keys(ads).some((k) => k !== "from"));
  return (
    <div className="rounded-btn border border-border bg-background/60 px-3 py-2.5" data-testid="contact-where-from">
      <span className="text-[11.5px] font-semibold">{t("Откуда клиент")}</span>
      <ul className="mt-1 flex flex-col gap-0.5 text-[12px]">
        {lines.map((l) => (
          <li key={l} className="flex gap-1.5">
            <i className="ri-megaphone-line mt-0.5 text-muted-foreground" aria-hidden />
            <span>{l}</span>
          </li>
        ))}
        {!hasAds ? (
          <li className="flex gap-1.5 text-muted-foreground">
            <i className="ri-question-line mt-0.5" aria-hidden />
            <span>{t("Рекламных меток нет: ни у контакта, ни у его сделок и лидов в Zoho CRM не записаны utm-метки, объявление или источник.")}</span>
          </li>
        ) : null}
        {origin ? (
          <li className="flex gap-1.5 text-muted-foreground">
            <i className="ri-user-add-line mt-0.5" aria-hidden />
            <span>
              {t("Карточка контакта — {origin}.", { origin: ORIGIN[origin] ?? origin })}
              {hasAds && ads?.from ? ` ${t("Метки взяты из {from}.", { from: FROM[ads.from] ?? ads.from })}` : ""}
            </span>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

export function ContactDialogs({ contactId, companyId, compact }: { contactId?: string; companyId?: string; compact?: boolean }) {
  const key = companyId ? `co:${companyId}` : `ct:${contactId}`;
  const listApi = companyId ? () => companiesApi.conversations(companyId) : () => contactsApi.conversations(contactId!);
  const searchApi = companyId ? (s: string) => companiesApi.searchMessages(companyId, s) : (s: string) => contactsApi.searchMessages(contactId!, s);
  const [rows, setRows] = useState<Conversation[] | null>(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<{ items: (Message & { channel: string; conversationName: string })[]; total: number } | null>(null);
  const [error, setError] = useState("");
  const [all, setAll] = useState(false);

  useEffect(() => {
    setRows(null);
    setQ("");
    setHits(null);
    setAll(false);
    listApi().then((r) => setRows(r.items), (e) => setError(apiErrorText(e)));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) {
      setHits(null);
      return;
    }
    const tm = setTimeout(() => searchApi(s).then(setHits, (e) => setError(apiErrorText(e))), 300);
    return () => clearTimeout(tm);
  }, [q, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = all || compact === false ? (rows ?? []) : (rows ?? []).slice(0, 6);
  return (
    <div className={cn(!compact && "rounded-btn border border-border bg-background/60 px-3 py-2.5")}>
      {compact ? null : (
        <div className="flex items-center gap-2">
          <span className="flex-1 text-[11.5px] font-semibold">
            {t("Диалоги по всем каналам")}
            {rows?.length ? ` · ${rows.length}` : ""}
          </span>
        </div>
      )}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Найти в переписке: слово или фраза")} aria-label={t("Поиск по переписке")} className={cn(input, "mt-1.5")} />
      {hits ? (
        <ul className="mt-1.5 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
          {hits.items.map((m) => (
            <li key={m.id}>
              <button type="button" onClick={() => open(m.conversationId)} className="flex w-full cursor-pointer flex-col gap-0.5 px-2.5 py-1.5 text-start transition hover:bg-muted/60">
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <i className={icon(m.channel)} aria-hidden />
                  {channelLabel(m.channel)} · {when(m.sentAt)} · {m.fromMe ? t("мы") : m.kind === "note" ? t("заметка") : t("клиент")}
                </span>
                <span className="line-clamp-2 text-[12px]">{m.text}</span>
              </button>
            </li>
          ))}
          {hits.items.length === 0 ? <li className="px-2.5 py-2 text-[12px] text-muted-foreground">{t("Ничего не найдено.")}</li> : null}
          {hits.total > hits.items.length ? <li className="px-2.5 py-1.5 text-[11px] text-muted-foreground">{t("Показаны {n} из {total} — уточните запрос.", { n: hits.items.length, total: hits.total })}</li> : null}
        </ul>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-1">
          {shown.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => open(c.id)} className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[12px] transition hover:bg-muted/60">
                <i className={icon(c.channel)} aria-hidden />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold">{channelLabel(c.channel)}</span>
                    {c.channelName ? <span className="truncate text-[11px] text-muted-foreground">{c.channelName}</span> : null}
                    {c.status === "closed" ? <span className="text-[11px] text-muted-foreground">· {t("закрыт")}</span> : null}
                    {c.assignee ? <span className="text-[11px] text-muted-foreground">· {c.assignee}</span> : null}
                  </span>
                  <span className="truncate text-[11.5px] text-muted-foreground">{c.lastMessageText || t("без сообщений")}</span>
                </span>
                <span className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">{listTime(c.lastMessageAt)}</span>
                {c.unread ? <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">{c.unread}</span> : null}
              </button>
            </li>
          ))}
          {rows && rows.length === 0 ? <li className="px-2 py-1 text-[11.5px] text-muted-foreground">{t("Диалогов пока нет — они появятся, когда клиент напишет или ему напишут из «Входящих».")}</li> : null}
          {rows && rows.length > shown.length ? (
            <li>
              <button type="button" onClick={() => setAll(true)} className="px-2 text-[12px] font-semibold text-primary hover:underline">
                {t("Показать все ({n})", { n: rows.length })}
              </button>
            </li>
          ) : null}
        </ul>
      )}
      {error ? <p className="mt-1 text-[11.5px] text-red-500">{error}</p> : null}
    </div>
  );
}
